import { CampaignSchedulerAttribute } from '@mm/state/model/CampaignsModel';
import {
  HandlerMethodTypes,
  StateUpdaterMethodTypes,
  LifeCycleThis,
  colors,
} from '@mm/utils';
import ArrowIcon, { ARROW_TYPES } from '@mm/icons/general/ArrowIcon';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import React from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { compose, withHandlers, withStateHandlers, lifecycle } from 'recompose';
import { ReactComponent as MoreIcon } from 'assets/more-icon.svg';
import DropdownMenu from '../../../../../../common/components/DropdownMenu/DropdownMenu';
import PrimarySelect from '../../../../../../common/components/PrimarySelect/PrimarySelect';
import './TimelineHeader.pcss';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';

export type Granularity = 'Minutes' | 'Hours' | 'Days' | 'Weeks' | 'Months';
export const GranularitySteps = new Map<Granularity, number>([
  ['Minutes', 1],
  ['Hours', 60],
  ['Days', 60 * 24],
  ['Weeks', 60 * 24 * 7],
  ['Months', 60 * 24 * 30],
]);

type OuterProps = {
  readonly?: boolean;
  scheduler: CampaignSchedulerAttribute;
  onChangeScheduler: (scheduler: CampaignSchedulerAttribute) => void;
  deleteScheduler: (scheduler: CampaignSchedulerAttribute) => void;
  dragHandleProps: { [key: string]: any };
  editScheduleDisabled: boolean;
  savedCampaignCanceled: boolean;
  campaignActive: boolean;
  isExpanded: boolean;
  setIsExpanded: (v: boolean) => void;
  platforms: { sms: boolean; facebook: boolean; instagram: boolean };
};

type InitialState = ReturnType<typeof initialState>;
type StateUpdaters = StateUpdaterMethodTypes<
  InitialState,
  OuterProps,
  typeof stateUpdaters
>;

type HandlerProps = OuterProps & InitialState & StateUpdaters;
type HandlerMethods = HandlerMethodTypes<HandlerProps, typeof handlers>;

type InnerProps = InitialState & StateUpdaters & HandlerMethods;

type Props = InnerProps & OuterProps;

export function pickGranularity(minutes: number): [number, Granularity] {
  if (minutes === 0) {
    return [minutes, 'Minutes'];
  }
  let selectedGranularity: Granularity = 'Minutes';
  let divisor = 1;
  GranularitySteps.forEach((value, granularity) => {
    if (minutes % value === 0) {
      selectedGranularity = granularity;
      divisor = value;
    }
  });

  return [minutes / divisor, selectedGranularity];
}

const initialState = (props: OuterProps) => {
  const [counter, granularity] = pickGranularity(props.scheduler.intervalInMin);
  return {
    active: false,
    counter,
    granularity,
  };
};
const stateUpdaters = {
  setActive: () => (active: boolean) => ({ active }),
  setCounter: () => (counter: number) => ({ counter }),
  setGranularity: () => (granularity: Granularity) => ({ granularity }),
};

const updateValue =
  (props: HandlerProps) => (counter: number, granularity: Granularity) => {
    const { scheduler, onChangeScheduler, setCounter, setGranularity } = props;
    setCounter(counter);
    setGranularity(granularity);
    onChangeScheduler({
      ...scheduler,
      intervalInMin: counter * (GranularitySteps.get(granularity) || 1),
    });
  };

const handlers = {
  updateValue,
};

function componentDidUpdate(this: LifeCycleThis<Props>, prevProps: Props) {
  const locallyCalculatedInterval =
    this.props.counter * (GranularitySteps.get(this.props.granularity) || 1);
  if (this.props.scheduler.intervalInMin !== locallyCalculatedInterval) {
    const [counter, granularity] = pickGranularity(
      this.props.scheduler.intervalInMin,
    );
    this.props.setCounter(counter);
    this.props.setGranularity(granularity);
    this.props.setActive(false);
  }
}

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers(initialState, stateUpdaters),
  withHandlers(handlers),
  lifecycle<Props, {}>({ componentDidUpdate }),
);

const useDropdownMenuList = (props: Props) => {
  const { deleteScheduler, scheduler } = props;
  const facebookPage = useSelector((state: StoreState) =>
    activeFacebookPageSelector(state),
  );

  const onClickPreview = () => {
    if (facebookPage) {
      window.open(
        `http://m.me/${facebookPage.remoteId}?ref=${scheduler.destinationPageSearchToken}`,
      );
    }
  };

  return [
    {
      name: 'Preview',
      onClick: onClickPreview,
    },
    {
      className: 'timeline-header__delete-button',
      name: 'Delete',
      onClick: () => deleteScheduler(scheduler),
    },
  ];
};

export const granularityOptions = Array.from(GranularitySteps.keys()).map(
  name => ({
    id: name,
    name,
  }),
);

const TimelineHeader = (props: Props) => {
  const {
    scheduler,
    editScheduleDisabled,
    updateValue,
    counter,
    granularity,
    savedCampaignCanceled,
    dragHandleProps,
    campaignActive,
    isExpanded,
    setIsExpanded,
    platforms,
  } = props;
  const minCounterValue = scheduler.sortOrder === 0 ? 0 : 1;
  const dropdownMenuList = useDropdownMenuList(props);
  const text =
    scheduler.sortOrder === 0
      ? 'after contacts enter audience(s)'
      : 'after the last message was sent';
  const granularityOptionsValid = granularityOptions.filter(option =>
    ['Days', 'Weeks', 'Months'].includes(option.id) ? platforms.sms : true,
  );
  const granularityValue =
    granularityOptionsValid.find(option => option.id === granularity) ||
    granularityOptionsValid[0];
  const fullText = editScheduleDisabled
    ? `${counter} ${granularityValue.name} ${text}`
    : text;
  const direction = isExpanded ? 'top' : 'bottom';

  const updateTimeWithValidation = (
    counter: number,
    granularity: Granularity,
  ) => {
    if (!platforms.sms) {
      return updateValue(
        Math.min(counter, granularity === 'Hours' ? 23 : 1380),
        granularity,
      );
    }
    updateValue(counter, granularity);
  };

  return (
    <div
      className={classnames('timeline-header', {
        'timeline-header--expanded': isExpanded,
      })}
    >
      <div className="timeline-header__drag-container" {...dragHandleProps} />
      <div className="timeline-header__left-section">
        <ArrowIcon
          direction={direction}
          color={colors.accentBlue}
          size={15}
          arrowType={ARROW_TYPES.CHEVRON}
          className="timeline-header__arrow-icon"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        {!editScheduleDisabled && (
          <>
            <input
              type="number"
              className="timeline-header__counter"
              onChange={e =>
                updateTimeWithValidation(
                  parseInt(e.target.value, 10) || minCounterValue,
                  granularity,
                )
              }
              value={counter}
              min={minCounterValue}
              max={
                !platforms.sms
                  ? granularityValue.id === 'Hours'
                    ? 23
                    : 1380
                  : undefined
              }
            />

            <PrimarySelect
              className="timeline-header__granularity-select"
              onChange={item =>
                updateTimeWithValidation(counter, item.name as Granularity)
              }
              options={granularityOptionsValid}
              value={granularityValue}
              customDropdownStyles={{ zIndex: '999' }}
            />
          </>
        )}

        <div className="timeline-header__text">{fullText}</div>
      </div>

      {!savedCampaignCanceled && (
        <DropdownMenu
          className={classnames('timeline-header__more-dropdown', {
            'timeline-header__active': campaignActive,
          })}
          menuItems={dropdownMenuList}
          menuButton={onMenuClick => (
            <MoreIcon
              onClick={onMenuClick}
              className="timeline-header__more-icon"
            />
          )}
        />
      )}
    </div>
  );
};

export default enhance(TimelineHeader);
